import { ReactNode, useEffect, useRef } from "react";

import { FormControlLabel, Grid, Switch } from "@mui/material";
import { FormikTextField } from "@shared";
import { FormikProps } from "formik";

import { ClientSelect } from "@/common/components/client-select/ClientSelect";
import { formatClientOptionLabel } from "@/common/components/client-select/ClientSelect.utils";
import { FormikAutocomplete } from "@/common/components/fields/formik/formik-autocomplete/FormikAutocomplete";
import { IOptionItem } from "@/common/components/fields/formik/formik-autocomplete/FormikAutocomplete.types";
import { FormikSystemDropdownAutocomplete } from "@/common/components/fields/formik/formik-autocomplete/FormikSystemDropdownAutocomplete";
import { FormikDatePicker } from "@/common/components/fields/formik/formik-date-picker/FormikDatePicker";
import FormikTimePicker from "@/common/components/fields/formik/formik-time-picker/FormikTimePicker";
import { nameof } from "@/common/helper/commonHelper";
import { SystemDropdownOptions } from "@/common/hooks/useSystemDropdownOptions";
import { useTranslation } from "@/translations";

import { AppointmentClientSelection, AppointmentFormValues, AppointmentMaxLength } from "./AppointmentForm.types";
import { isKnownPurpose, isOverbookablePurpose } from "./AppointmentForm.utils";
import { DefaultAppointmentLocation } from "./appointmentConstants";
import { AppointmentClientField } from "./components/appointment-client-field/AppointmentClientField";
import { ClientNotificationSection } from "./components/client-notification-section/ClientNotificationSection";
import { useEndFollowingStart } from "./hooks/useEndFollowingStart";

interface Props<T extends AppointmentFormValues> {
  formik: FormikProps<T>;
  locationOptions: string[];
  purposeOptions: IOptionItem[];
  purposes: SystemDropdownOptions;
  onIsAllDayChange: (checked: boolean) => void;
  /** The client already assigned to the appointment (edit dialog); shown read-only. */
  assignedClient?: AppointmentClientSelection | null;
  /** Rendered on its own row below Zweck / Ort. */
  counselorField?: ReactNode;
}

export function AppointmentFormFields<T extends AppointmentFormValues>({
  formik,
  locationOptions,
  purposeOptions,
  purposes,
  onIsAllDayChange,
  assignedClient,
  counselorField,
}: Props<T>) {
  const { t } = useTranslation();

  const { purposeKey, location } = formik.values;
  const isConfiguredPurpose = isKnownPurpose(purposeKey, purposes);
  const notificationClient = assignedClient ?? formik.values.client;
  const { setFieldValue } = formik;
  const prevPurposeRef = useRef(purposeKey);
  const handleStartChange = useEndFollowingStart(formik, "startDate", "endDate");

  useEffect(() => {
    if (purposeKey === prevPurposeRef.current) {
      return;
    }
    prevPurposeRef.current = purposeKey;

    if (isKnownPurpose(purposeKey, purposes) && !location) {
      void setFieldValue(nameof<AppointmentFormValues>("location"), DefaultAppointmentLocation);
    }
  }, [purposeKey, location, setFieldValue, purposes]);

  return (
    <Grid container rowSpacing={2} columnSpacing={2}>
      <Grid size={12}>
        <FormControlLabel
          control={
            <Switch
              checked={formik.values.isAllDay}
              onChange={(_, checked) => onIsAllDayChange(checked)}
              name="isAllDay"
            />
          }
          label={t("calendar.appointmentCreation.isAllDay")}
        />
      </Grid>
      <Grid size={3}>
        <FormikDatePicker
          formik={formik}
          fieldName="startDate"
          label={t("calendar.appointmentCreation.startDate")}
          required
          onValueChange={handleStartChange}
        />
      </Grid>
      <Grid size={3}>
        <FormikTimePicker
          formik={formik}
          fieldName="startDate"
          label={t("calendar.appointmentCreation.startTime")}
          disabled={formik.values.isAllDay}
          required
          onValueChange={handleStartChange}
        />
      </Grid>
      <Grid size={3}>
        <FormikDatePicker
          formik={formik}
          fieldName="endDate"
          label={t("calendar.appointmentCreation.endDate")}
          required
        />
      </Grid>
      <Grid size={3}>
        <FormikTimePicker
          formik={formik}
          fieldName="endDate"
          label={t("calendar.appointmentCreation.endTime")}
          disabled={formik.values.isAllDay}
          required
        />
      </Grid>
      <Grid size={6}>
        <FormikSystemDropdownAutocomplete
          formik={formik}
          fieldName="purposeKey"
          label={t("calendar.appointmentCreation.purposeName")}
          required
          options={purposeOptions}
          helperText={
            isConfiguredPurpose
              ? `${purposes.labelOf(purposeKey)} · ${t(
                  "calendar.appointmentCreation.purposeStatus.categoryAppointment"
                )} · ${
                  isOverbookablePurpose(purposeKey, purposes)
                    ? t("calendar.appointmentCreation.purposeStatus.bookable")
                    : t("calendar.appointmentCreation.purposeStatus.notBookable")
                }`
              : undefined
          }
        />
      </Grid>
      <Grid size={6}>
        <FormikAutocomplete
          formik={formik}
          fieldName="location"
          freeSolo
          label={t("calendar.appointmentCreation.location")}
          options={locationOptions}
          required={isConfiguredPurpose}
        />
      </Grid>
      {counselorField && <Grid size={6}>{counselorField}</Grid>}
      <Grid size={12}>
        <FormikTextField
          formik={formik}
          fieldName="details"
          label={t("calendar.appointmentCreation.details")}
          multiline
          rows={3}
          slotProps={{ input: { inputProps: { maxLength: AppointmentMaxLength.details } } }}
        />
      </Grid>
      <Grid size={12}>
        {assignedClient ? (
          <AppointmentClientField
            clientId={assignedClient.clientId}
            clientName={assignedClient.name}
            markedForRemoval={formik.values.clientMarkedForRemoval}
            onMarkForRemoval={() => void setFieldValue(nameof<AppointmentFormValues>("clientMarkedForRemoval"), true)}
            onUndoRemoval={() => void setFieldValue(nameof<AppointmentFormValues>("clientMarkedForRemoval"), false)}
          />
        ) : (
          <ClientSelect
            label={t("common.client")}
            // Without a selected client nothing follows the select, so its reserved blank helper
            // line would stack onto the dialog's bottom padding and widen the trailing gap.
            disableHelperSpace={!notificationClient}
            onChange={(selection) => {
              const client: AppointmentClientSelection | null =
                selection && typeof selection !== "string"
                  ? {
                      clientId: selection.clientId,
                      name: formatClientOptionLabel(selection),
                      phone: selection.phone ?? null,
                      email: selection.email ?? null,
                    }
                  : null;

              void setFieldValue(nameof<AppointmentFormValues>("client"), client);
              // Selecting (or clearing) a client resets the channels: SMS is the default, but only
              // when a phone number is stored — a channel without contact data cannot be sent.
              void setFieldValue(nameof<AppointmentFormValues>("notifyBySms"), !!client?.phone);
              void setFieldValue(nameof<AppointmentFormValues>("notifyByEmail"), false);
            }}
          />
        )}
      </Grid>
      {notificationClient && (
        <Grid size={12}>
          <ClientNotificationSection
            formik={formik}
            phone={notificationClient.phone}
            email={notificationClient.email}
            cancellation={formik.values.clientMarkedForRemoval}
          />
        </Grid>
      )}
    </Grid>
  );
}
